import type { UIStrings, SEOMetadata } from './base'

export const jaStrings: UIStrings = {
  common: {
    loading: '読み込み中...',
    error: 'エラーが発生しました',
    success: '成功',
    cancel: 'キャンセル',
    confirm: '確認',
    close: '閉じる',
    back: '戻る',
    next: '次へ',
    previous: '前へ'
  },

  nav: {
    home: 'ホーム',
    about: 'SrcRankについて',
    guides: '最適化ガイド',
    languageSwitch: '言語',
    selectLanguage: '言語を選択'
  },

  header: {
    title: '源策 (Gensaku)',
    subtitle: 'SEO / AEO / GEO ツール',
    description:
      '検索システムに理解されやすいか、生成 AI に抽出されやすいか、引用されやすいかを確認するためのツールです。'
  },

  hero: {
    intro:
      '本ツールは台湾の SEO スペシャリスト<strong>邱煜庭（ブラック先生）</strong>が長年の実務研究を経て構築したものです。Google 公式の「検索品質評価ガイドライン」、Helpful Content Update (HCU)、各種国際 SEO 評価基準を組み合わせ、コンテンツが検索システムに理解されやすいか、生成 AI に抽出されやすいか、そして AI モデルに引用される可能性を高められるかを診断します。AEO / GEO をより体系的に学びたい場合は、ブラック先生の <a href="https://aeogeo.thinkwithblack.com/" target="_blank" rel="sponsored noopener noreferrer">「內容結構學」</a> に参加し、AI に引用されやすいサイト構造の土台づくりを学べます。',
    disclaimer:
      '免責事項：本ツールは第三者による診断および最適化提案のみを提供します。検索順位やトラフィックの成長を保証するものではありません。'
  },

  dashboard: {
    totalScore: '総合スコア',
    structureScore: '構造スコア',
    strategyScore: '戦略スコア',
    why: 'WHY：課題の定義',
    how: 'HOW：実行方法',
    what: 'WHAT：ソリューション',
    guide: 'ガイド',
    viewGuide: '最適化ガイドを表示',
    noGuideAvailable: 'ガイドは利用できません',
    failedToLoadGuide: '最適化ガイドを読み込めませんでした'
  },

  guides: {
    title: '最適化ガイド',
    description: '詳細なコンテンツ最適化の推奨事項',
    optimization: '最適化ガイド',
    reasons: '根本原因',
    diagnosis: '診断方法',
    improvements: '改善戦略',
    actions: 'アクションプラン',
    faq: 'よくある質問',
    quickReference: 'クイックリファレンス'
  },

  analysis: {
    submitEmail: 'メールアドレスを送信',
    emailPlaceholder: 'メールアドレスを入力してください',
    submit: '送信',
    submitting: '送信中...',
    checkResults: '結果を確認',
    resultsWillBeSent: '分析結果はメールで送信されます',
    enterValidEmail: '有効なメールアドレスを入力してください',
    asyncTitle: '長時間ジョブ通知モード',
    asyncDescription: '通常の既定フローではありません。待ち時間が長そうなときだけ、背景処理に切り替えて完了後に完全な結果をメールで受け取ります。',
    queuedTitle: '分析を受け付けました',
    queuedLine1: 'この分析はバックグラウンド処理に切り替わりました。ページ上で待ち続ける必要はありません。',
    queuedLine2: '完了後、完全な結果を次のメールアドレスへ送信します。',
    taskIdLabel: 'タスク ID',
    durationHint: '分析には通常 1〜5 分ほどかかります。メール表示に問題があっても、専用の結果ページから同じ結果を確認できます。',
    helperTip: 'ヒント：長文や時間がかかりそうな分析だけ、このモードを使うのが適切です。',
    submitFailed: '送信に失敗しました。もう一度お試しください。'
  },

  email: {
    subject: '✅ コンテンツ分析結果のご案内',
    headerTitle: '✨ 分析が完了しました',
    headerSubtitle: 'AI 戦略評価が完了しました',
    scoreLabel: '総合スコア / 100',
    structureWeight: '構造スコア · 40% ウェイト',
    strategyWeight: '戦略スコア · 60% ウェイト',
    recommendationsTitle: '優先アクションリスト',
    recommendationsDescription: '赤色の項目から着手すると、スコア改善のリターンが最大化します。',
    recommendationsEmpty: '素晴らしいです！今すぐ対応が必要な項目はありません。',
    todoExampleLabel: '例',
    viewButton: '詳細結果を見る',
    viewButtonDescription: 'ボタンをクリックして、スコア内訳・WHY/HOW/WHAT分析・改善提案を確認してください。',
    taskIdLabel: 'タスク ID',
    footerNotice: '© 2025 SrcRank | ',
    footerNoReply: '本メールは自動送信です。直接のご返信はご遠慮ください。',
    interpretation: {
      excellent: '🌟 優秀 - コンテンツ品質が非常に高く、AI に引用される可能性があります',
      good: '👍 良好 - 基礎は十分、さらなる最適化で伸びしろあり',
      fair: '⚠️ 中程度 - 改善が必要です。推奨項目をご確認ください',
      poor: '❌ 改善が必要 - コンテンツ戦略の見直しを検討してください'
    },
    priorityLabelHigh: '高優先度',
    priorityLabelMedium: '中優先度',
    priorityLabelLow: '低優先度',
    textTitle: 'AI コンテンツ最適化レポート',
    textScoreHeading: '総合スコア',
    textBreakdownHeading: 'スコア内訳',
    textRecommendationsHeading: '優先アクションリスト',
    textViewHeading: '詳細結果を見る'
  },

  metrics: {
    intentFit: '検索意図の適合度',
    helpfulRatio: 'ヘルプフルレシオ',
    depthCoverage: 'コンテンツの深さと網羅性',
    intentExpansion: '意図の拡張とキーワードカバレッジ',
    actionability: '実行可能性',
    readabilityRhythm: '可読性と物語のリズム',
    structureHighlights: '構造化ハイライト',
    authorBrandSignals: '著者・ブランド認識',
    evidenceSupport: '信頼できる証拠と引用',
    experienceSignals: '一次経験とケーススタディ',
    narrativeDensity: '叙述の具体性と情報密度',
    freshnessSignals: '新鮮さと更新シグナル',
    expertPerspective: '専門家の視点と判断力',
    extractability: '回答抽出性',
    keySummary: '重要な要約とハイライト',
    conversationalGuidance: '会話的な語り口とガイダンス',
    readerActivation: '読者エンゲージメントとフォローアップ'
  },

  status: {
    excellent: '優秀',
    good: '良好',
    fair: 'まあまあ',
    poor: '改善が必要',
    veryPoor: '緊急改善が必要'
  },

  errors: {
    networkError: 'ネットワーク接続エラー',
    serverError: 'サーバーエラー',
    invalidInput: '無効な入力',
    notFound: '見つかりません',
    unauthorized: '権限がありません'
  },

  input: {
    contentLabel: 'コンテンツ本文',
    contentPlaceholder: 'ここに記事コンテンツを貼り付けてください...',
    wordCountLabel: '文字数',
    wordCountUnit: '文字',
    keywordsLabel: 'ターゲットキーワード',
    keywordsHint: '（必須・1〜5語。Enter またはカンマで追加）',
    keywordsPlaceholder: '例：ai tools',
    deliveryModeLabel: '結果の受け取り方法',
    deliveryModeHint: '通常は即時分析のまま使います。待ち時間が長そうなときだけ、メール通知モードに切り替えます。',
    deliverySyncLabel: 'すぐ結果を見る',
    deliveryAsyncLabel: '完了後にメールで受け取る',
    deliveryAsyncDescription: '長文や時間がかかりそうな分析向けです。ページを離れても、完了後に完全な結果をメールで受け取れます。',
    emailLabel: 'メールアドレス',
    emailOptionalHint: '（長時間ジョブの通知モードで使用）',
    emailRequiredHint: '（必須。完了後に完全な結果を送信します）',
    emailPlaceholder: 'your.email@example.com',
    submitSync: 'AI 分析を開始',
    submitAsync: 'バックグラウンド分析を開始し、完了後にメール送信',
    submitLoading: '分析中...',
    errorEmptyContent: '記事内容を入力してください。',
    errorKeywordsRequired: 'ターゲットキーワードを1〜5語入力してください。',
    errorKeywordsMax: 'ターゲットキーワードは最大5語までです。',
    errorInvalidEmail: '有効なメールアドレスを入力してください。',
    analyzing: 'AI がコンテンツを分析中です...',
    analyzingHint: 'これには数秒かかる場合があります。お待ちください。'
  },

  results: {
    whyTitle: 'なぜこの分析に注目すべきなのか？',
    whyDescription: 'なぜこの分析に注目すべきなのか？',
    howTitle: 'スコアはどのように計算されるのか？',
    howDescription: 'スコアはどのように計算されるのか？',
    whatTitle: '具体的にどのように改善できるのか？',
    whatDescription: '具体的にどのように改善できるのか？',
    overallScoreTitle: 'v5 総合スコア',
    overallScoreDescription: '最新 v5 スコアリングモデル、構造（40%）と戦略（60%）の加重総合パフォーマンス。',
    noV5ScoreFallback: 'v5 スコアはまだ利用できません。以下は従来の総合スコアです。',
    structureScoreTitle: '構造スコア',
    structureScoreDescription: 'コンテンツが良好な構造、可読性、証拠、経験サポートを備えているかを確認します。',
    strategyScoreTitle: '戦略スコア',
    strategyScoreDescription: 'Why / How / What 戦略フレームワークが完全であり、ターゲットオーディエンスと共鳴しているかを評価します。',
    priorityRecommendations: '優先改善推奨事項',
    priorityRecommendationsDescription: 'v5 スコアリングの構造・戦略の側面に基づいて、以下の推奨事項を優先的に処理することで、総合スコアを迅速に向上させることができます。',
    structureInsights: '構造インサイト',
    structureInsightsDescription: '段落構造、要約整理、会話トーン、読者エンゲージメントに焦点を当てて、コンテンツをより読みやすく、理解しやすくします。',
    strategyInsights: '戦略インサイト',
    strategyInsightsDescription: 'ヘルプフルネス、コンテンツの深さ、信頼性、キーワードカバレッジをカバーして、コンテンツを検索とターゲットリーダーのニーズに合わせます。',
    sourceTextReview: 'ソーステキストレビュー',
    sourceTextReviewDescription: 'ソースコンテンツを段落ごとにレビューし、上記の推奨事項と組み合わせてテキスト、例、構造を調整します。',
    paragraph: '段落',
    originalContent: 'オリジナルコンテンツ',
    tokens: 'トークン',
    segments: 'セグメント',
    format: 'フォーマット',
    collapse: '折りたたむ',
    expand: '展開',
    keySignals: 'キーシグナル',
    evidencePoints: '証拠ポイント',
    noMetricsAvailable: '現在、関連するメトリクスデータは利用できません。',
    insufficientMetadata: 'スコアリングできません：HTML メタデータが不足しています',
    metadataUnavailable: 'メタデータ検出可能',
    schemaUnavailable: 'スキーマ検出可能',
    undetectableItems: '検出不可能なアイテム',
    hint: 'ヒント：完全なページ HTML を直接貼り付けるか、ソースコードを提供する API を使用してください。これにより、システムが Meta / Schema / 作成者情報などの重要なタグにアクセスできます。',
    detectionStatus: '現在の検出ステータス',
    yes: 'はい',
    no: 'いいえ',
    loadGuideError: '最適化ガイドを読み込めません。後でもう一度お試しください。',
    loadGuideErrorRetry: 'ガイドの読み込み中にエラーが発生しました。後でもう一度お試しください。',
    notEvaluatedYet: 'まだ評価されていません',
    excellentPerformance: '優れたパフォーマンス',
    canBeImproved: '改善の余地あり',
    priorityImprovement: '優先改善',
    urgentImprovement: '緊急改善が必要',
    weight: 'ウェイト',
    highPriority: '高優先度',
    mediumPriority: '中優先度',
    lowPriority: '低優先度',
    suggestion: '提案',
    category: 'カテゴリー',
    lowScoreWarning: 'このメトリクスは現在パフォーマンスが低くなっています。改善を優先し、以下のメトリクスと推奨事項リストを参照して具体的なアクションを見つけることをお勧めします。',
    metricHelpfulRatio: 'Helpful Ratio',
    metricIntentFit: '検索意図の適合度',
    metricDepthCoverage: 'コンテンツの深さと網羅性',
    metricIntentExpansion: '意図の拡張とキーワードカバレッジ',
    metricActionability: '実行可能性',
    metricReadabilityRhythm: '可読性と物語のリズム',
    metricStructureHighlights: '構造化ハイライト',
    metricAuthorBrandSignals: '著者・ブランド認識',
    metricEvidenceSupport: '信頼できる証拠と引用',
    metricExperienceSignals: '一次経験とケーススタディ',
    metricNarrativeDensity: '物語の具体性と情報密度',
    metricFreshnessSignals: '新鮮さと更新シグナル',
    metricExpertPerspective: '専門家の視点と判断力',
    metricExtractability: '回答抽出性',
    metricKeySummary: '重要な要約とハイライト',
    metricConversationalGuidance: '会話的な語り口とガイダンス',
    metricReaderActivation: '読者エンゲージメントとフォローアップ',
    metricHelpfulRatioDesc: 'HCU シグナルに基づいて、有用なコンテンツと有用でない、またはトピックから外れたコンテンツの比率を測定します。',
    metricIntentFitDesc: 'コンテンツが検索意図にどの程度適合し、最初の段落で回答を提供しているかを評価します。',
    metricDepthCoverageDesc: 'コンテンツの深さ、トピックの一貫性、セマンティック フォーカスを評価して、包括的なカバレッジを確保します。',
    metricIntentExpansionDesc: 'コンテンツが関連する質問に対処し、メイン トピック以外の関連キーワードをカバーしているかを確認します。',
    metricActionabilityDesc: '明確なステップとガイダンスを含む、コンテンツの実行可能性と実用性を測定します。',
    metricReadabilityRhythmDesc: '文構造、段落の長さ、物語の流れを評価して、最適な可読性を確保します。',
    metricStructureHighlightsDesc: 'リスト、表、その他の構造要素を使用して情報を整理する方法を評価します。',
    metricAuthorBrandSignalsDesc: '著者情報とブランド認識シグナルをチェックして、信頼性を確立します。',
    metricEvidenceSupportDesc: '引用、データ、外部参照の存在を評価して、主張をサポートします。',
    metricExperienceSignalsDesc: 'コンテンツ内の一次経験、ケーススタディ、実例を測定します。',
    metricNarrativeDensityDesc: 'ナレーティブと例の具体性と情報密度を評価します。',
    metricFreshnessSignalsDesc: '最近の日付、更新された情報、新鮮さのインジケーターをチェックします。',
    metricExpertPerspectiveDesc: '専門用語、専門的判断、比較分析を評価します。',
    metricExtractabilityDesc: 'AI システムが個々の段落から回答を抽出できる容易さを測定します。',
    metricKeySummaryDesc: 'キーポイント、要約、強調表示された情報をチェックして、素早いスキャンを可能にします。',
    metricConversationalGuidanceDesc: 'コンテンツ全体の会話的なトーンと読者ガイダンス キューを評価します。',
    metricReaderActivationDesc: '読者のインタラクションを促すための CTA やフォローアップ質問などのエンゲージメント要素を測定します。',
    categoryContent: 'コンテンツ',
    categoryTrust: '信頼性',
    categoryExperience: '読者体験',
    categoryStructure: '構造',
    categoryStrategy: '戦略'
  },

  scoreCard: {
    scoreComposition: 'スコア構成',
    expandExplanation: '説明を表示',
    collapseExplanation: '説明を非表示',
    excellent: '優秀',
    good: '良好',
    fair: '中程度',
    needsImprovement: '改善が必要',
    headingStructure: '見出し構造',
    contentOrganization: 'コンテンツ編成',
    readability: '可読性',
    evidence: '証拠と引用',
    experience: '実務経験',
    freshness: '新鮮さと更新',
    actionability: '実行可能性',
    semanticQuality: 'セマンティック品質',
    credibility: '信頼性',
    trustSignals: '信頼シグナル',
    why: 'Why（問題）',
    how: 'How（方法）',
    what: 'What（解決策）',
    overallScore: '全体戦略スコア',
    structure: '構造',
    strategy: '戦略',
    structureExplanationIntro: '段落レイアウト、可読性、証拠、経験などのコンテンツ構造シグナルは v5 スコアの 40% を占め、記事の骨組みを確保します。',
    strategyExplanationIntro: 'Why/How/What 戦略フレームワークの深さは 60% を占め、コンテンツが読者と検索ニーズに真に対応しているかを反映します。',
    headingStructureExpl: 'H1/H2 階層が明確で、タイトルがトピックに焦点を当てているかを確認し、読者と検索エンジンが素早く概要を把握できるようにします。',
    contentOrganizationExpl: '段落、リスト、表などの構造化要素の使用を評価して、情報が整理され、散らばっていないことを確認します。',
    readabilityExpl: '文の長さと長い段落の比率を測定して、言語の流れが滑らかで段落の長さが適切であることを確認し、読む負担を軽減します。',
    evidenceExpl: 'データ、年号、信頼できる情報源が引用されて主張をサポートし、信頼性を高めているかを確認します。',
    experienceExpl: '一人称の経験、ケーススタディ、実践的な洞察を検出して、EEAT の「経験」シグナルを強化します。',
    freshnessExpl: '更新日が開示され、最新の情報が引用されているかを確認し、古いコンテンツが検索信頼性に影響しないようにします。',
    actionabilityExpl: 'コンテンツがステップ、チェックリスト、または具体的な提案を提供して、読者がすぐに行動できるようにしているかを確認します。',
    semanticQualityExpl: 'HTML モードでは、セマンティックな自然さと段落フォーカスを評価して、キーワード詰め込みを避けます。',
    contentLengthExpl: 'プレーンテキスト モードでは、単語数と段落密度を評価して、包括的なトピック カバレッジを確保し、ペーシングを維持します。',
    whyExpl: 'このトピックが重要な理由を説明し、読者の痛点と文脈を具体的に描写して、信頼性を確立しているかを評価します。',
    howExpl: 'コンテンツが明確なステップ、方法、実行ガイドラインを提供して、読者が何をすべきかを知っているかを評価します。',
    whatExpl: '最終的な主張と行動喚起が明確で、読者が次のステップまたは得られる価値を理解するのに役立つかを確認します。',
    overallScoreExpl: 'Why/How/What の 3 つの側面全体で平均化された戦略パフォーマンス。全体的な説得力と物語の完全性を測定します。'
  },

  recommendations: {
    title: '最適化推奨事項',
    noRecommendations: '素晴らしい！現在、改善が必要な領域はありません。',
    example: '例',
    helpful: '役に立つ',
    notApplicable: '該当なし',
    categoryContent: 'コンテンツ',
    categoryTrust: '信頼',
    categoryExperience: '読者体験',
    categoryStructure: '構造',
    categoryStrategy: '戦略'
  },

  v5Dashboard: {
    analyzing: '分析中...',
    noResults: '利用可能な結果がありません',
    pleaseSubmit: 'コンテンツを送信して分析してください',
    overallScore: '総合スコア',
    outOf: '/100',
    excellent: '🌟 優秀 - 優れたコンテンツ品質',
    good: '👍 良好 - 基礎がしっかりしており、さらに最適化できます',
    fair: '⚠️ 中程度 - 改善が必要です',
    needsImprovement: '❌ 改善が必要 - コンテンツ戦略の見直しをお勧めします',
    structureScore: '構造スコア',
    strategyScore: '戦略スコア',
    structureDetails: '構造スコア詳細',
    strategyDetails: '戦略スコア詳細 (WHY/HOW/WHAT)',
    whyLabel: 'WHY - 問題定義',
    howLabel: 'HOW - 実装方法',
    whatLabel: 'WHAT - ソリューション',
    weight: 'ウェイト',
    suggestions: '改善提案',
    detectionStatus: '現在の検出ステータス',
    metadataDetectable: 'メタデータ検出可能',
    schemaDetectable: 'スキーマ検出可能',
    yes: 'はい',
    no: 'いいえ'
  },

  footer: {
    copy:
      '© 2025 SrcRank（<span>YuYan Consulting Co., Ltd. (TW)</span> と <a href="https://toldyou.co" target="_blank" rel="noopener noreferrer" class="text-primary-300 hover:text-primary-200 underline">燈言顧問株式会社 (JP)</a> により提供）All Rights Reserved.'
  },

  resultsPage: {
    missingTaskId: 'タスク ID がありません',
    loadingResults: '結果を読み込み中...',
    queryingResults: 'サーバーから分析結果を照会しています',
    queryFailed: '照会に失敗しました',
    noResults: '利用可能な結果がありません',
    checkTaskId: 'タスク ID が正しいかご確認ください',
    backToHome: 'ホームに戻る',
    analysisResults: '分析結果',
    taskId: 'タスク ID',
    completedAt: '完了時刻',
    submittedContent: '送信されたコンテンツ',
    characterCount: '文字数',
    keywords: 'キーワード',
    none: 'なし',
    strategyAnalysisDetails: '戦略分析の詳細',
    whyProblem: 'WHY - 問題定義',
    howImplementation: 'HOW - 実装方法',
    whatSolution: 'WHAT - ソリューション',
    evidence: '証拠',
    improvementSuggestions: '改善提案',
    notFound: '該当するタスクの結果が見つかりません。タスク ID が正しいか、または結果が期限切れ（7 日）でないかご確認ください。',
    resultExpired: '照会に失敗しました'
  },

  scoreHistory: {
    title: 'スコア追跡ダッシュボード',
    description: '最新 200 件の分析を保持し、定期的な追跡と自動エクスポート プロセスを確立します。',
    exportButton: 'CSV をエクスポート',
    clearButton: '履歴をクリア',
    noHistory: 'まだ履歴レコードがありません。分析を完了すると、システムが自動的に追跡ダッシュボードに追加します。',
    averageOverallScore: '平均総合スコア',
    latestTrend: '最新トレンド',
    sevenDayAnalysis: '7 日間の分析',
    nextSchedule: '次のスケジュール',
    notScheduled: 'スケジュール設定なし',
    notScheduledHint: 'システムは最新の分析日 + 7 日を定期的なレビュー日として使用します。',
    trendComparison: '前回の総合スコアとの比較',
    sevenDayHint: 'コンテンツを最新の状態に保つため、週に最低 3 回の分析をお勧めします。',
    nextReviewHint: 'システムは最新の分析日 + 7 日を定期的なレビュー日として使用します。',
    latestRecords: '最新分析レコード',
    timeHeader: '時刻',
    keywordsHeader: 'キーワード',
    overallHeader: 'Overall',
    aeoHeader: 'AEO',
    seoHeader: 'SEO',
    gapHeader: 'ギャップ',
    weakFlagsHeader: '弱点フラグ',
    noGaps: 'なし',
    noWeakFlags: 'なし',
    exportProcess: 'エクスポート プロセス',
    maintenanceSuggestions: 'メンテナンスの提案',
    exportStep1: '分析を完了すると、ダッシュボードが自動的にレコードを追加します。',
    exportStep2: '「CSV をエクスポート」をクリックして、最新 200 件のレコードをダウンロードします。',
    exportStep3: 'CSV をデータ ウェアハウスまたは Google Sheet にアップロードして、長期的な追跡を行います。',
    maintenanceStep1: 'コンテンツを最新の状態に保つため、週に最低 3 回の分析を実施します。',
    maintenanceStep2: '毎月初めに前月の CSV エクスポートを整理し、KPI ダッシュボードを更新します。',
    maintenanceStep3: '連続して 2 回 60 点未満のスコアが出た場合は、高優先度の修正チケットを作成します。',
    flat: 'フラット'
  }
}

export const jaSEO: Record<string, SEOMetadata> = {
  home: {
    title: 'SEO/AEO/GEO ツール｜源策 (Gensaku): AI コンテンツ戦略アドバイザー',
    description: '源策 (Gensaku) はブラック先生が設計した SEO/AEO/GEO ツールです。検索システムに理解されやすいか、生成 AI に抽出されやすいか、AI に引用される情報源になりやすいかを確認できます。',
    keywords: ['SEO', 'AEO', 'GEO', 'コンテンツ最適化', 'AI分析', 'AI引用']
  },
  guides: {
    title: '最適化ガイド - コンテンツ最適化AI',
    description: '各スコアリング指標の詳細な最適化ガイド。スコアが低い理由を理解し、実行可能な改善戦略を取得します。'
  },
  analysis: {
    title: '非同期コンテンツ分析 - コンテンツ最適化AI',
    description: 'コンテンツを送信して非同期 AI スコアリングを実行。処理完了後、構造と戦略のインサイトをメールで受け取れます。',
    keywords: ['非同期分析', 'AI コンテンツ評価', 'メール結果', 'コンテンツ監査']
  },
  results: {
    title: '分析結果ダッシュボード - コンテンツ最適化AI',
    description: '完了した AI コンテンツ分析を確認し、構造・戦略スコアやギャップ診断、優先改善アクションを把握できます。',
    keywords: ['分析結果', 'コンテンツダッシュボード', 'SEO インサイト', 'AEO スコアリング']
  }
}
